/**
 * PurchaseTicketModal - 회원에게 카탈로그 수강권을 판매하는 모달 (폼 2-B)
 *
 * - 회원/센터 정보는 부모(AdminMemberDetailPage)가 prefill해 전달
 * - 카탈로그 목록은 모달 open 시 `getAdminTicketCatalogList`로 read
 * - `usePurchaseTicket` 훅으로 Cloud Function `purchaseTicket` 호출
 * - 성공 시 onCreated 콜백 → 부모가 모달 close + 회원 데이터 refetch
 */

import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { usePurchaseTicket } from "@/hooks/useTicketPurchase";
import {
  getAdminTicketCatalogList,
  type TicketCatalogItem,
} from "@/services/firestore/admin-ticket-catalog";

interface PurchaseTicketModalProps {
  isOpen: boolean;
  /** 카탈로그 read 용 (회원이 속한 센터의 owner uid) */
  ownerUid: string;
  /** 카탈로그 read 용 */
  centerId: number;
  /** 백엔드 호출에 전달 (lookupCenterByName 입력) */
  centerName: string;
  /** 백엔드 호출에 전달 + UI prefill 표시 */
  memberName: string;
  /** 백엔드 호출에 전달 + UI prefill 표시 */
  memberMobile: string;
  onClose: () => void;
  onCreated: () => void;
}

function getErrorMessage(error: unknown): string | null {
  if (error && typeof error === "object" && "message" in error) {
    const msg = (error as { message: unknown }).message;
    if (typeof msg === "string" && msg.length > 0) {
      return msg;
    }
  }
  return null;
}

function formatCatalogOption(item: TicketCatalogItem): string {
  const parts: string[] = [`${item.title} · ${item.type}`, `${item.totalCoupon}회`];
  if (item.validDays !== undefined) parts.push(`${item.validDays}일`);
  if (item.maxTrainee !== undefined) parts.push(`최대 ${item.maxTrainee}인`);
  return parts.join(" · ");
}

export function PurchaseTicketModal({
  isOpen,
  ownerUid,
  centerId,
  centerName,
  memberName,
  memberMobile,
  onClose,
  onCreated,
}: PurchaseTicketModalProps) {
  const [catalogs, setCatalogs] = useState<TicketCatalogItem[]>([]);
  const [isLoadingCatalogs, setIsLoadingCatalogs] = useState(false);
  const [catalogLoadError, setCatalogLoadError] = useState<string | null>(null);
  const [selectedTicketId, setSelectedTicketId] = useState("");

  const purchaseTicket = usePurchaseTicket();

  useEffect(() => {
    if (!isOpen) return;

    setSelectedTicketId("");
    setCatalogLoadError(null);
    purchaseTicket.reset();

    let cancelled = false;
    setIsLoadingCatalogs(true);
    getAdminTicketCatalogList(ownerUid, centerId)
      .then((items) => {
        if (!cancelled) setCatalogs(items);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setCatalogLoadError(
            err instanceof Error ? err.message : "수강권 카탈로그를 불러오지 못했습니다.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingCatalogs(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ownerUid, centerId]);

  if (!isOpen) return null;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!selectedTicketId) return;
    purchaseTicket.mutate(
      {
        centerName,
        memberName,
        memberMobile,
        ticketId: selectedTicketId,
      },
      {
        onSuccess: () => {
          onCreated();
        },
      },
    );
  };

  const serverErrorMessage = purchaseTicket.isError ? getErrorMessage(purchaseTicket.error) : null;
  const isSubmitDisabled = !selectedTicketId || purchaseTicket.isPending;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">수강권 구매</h2>
        <p className="text-sm text-gray-500 mb-4">센터: {centerName}</p>

        {/* 회원 정보 (readonly prefill) */}
        <div className="mb-4 p-3 bg-gray-50 rounded-md text-sm">
          <p>
            <span className="font-medium text-gray-500">회원:</span>{" "}
            <span className="text-gray-800">{memberName}</span>
          </p>
          <p className="mt-1">
            <span className="font-medium text-gray-500">연락처:</span>{" "}
            <span className="text-gray-800">{memberMobile}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* 카탈로그 dropdown */}
          <div className="mb-4">
            <label htmlFor="ticketCatalog" className="block text-sm font-medium text-gray-700 mb-1">
              수강권 <span className="text-red-500">*</span>
            </label>
            {isLoadingCatalogs ? (
              <p className="text-sm text-gray-500">카탈로그를 불러오는 중...</p>
            ) : catalogLoadError ? (
              <p className="text-sm text-red-600">{catalogLoadError}</p>
            ) : catalogs.length === 0 ? (
              <p className="text-sm text-gray-600">
                등록된 수강권이 없습니다. 먼저 수강권 카탈로그를 등록하세요.
              </p>
            ) : (
              <select
                id="ticketCatalog"
                value={selectedTicketId}
                onChange={(e) => setSelectedTicketId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white"
                required
              >
                <option value="">수강권 선택</option>
                {catalogs.map((item) => (
                  <option key={item.id} value={item.id}>
                    {formatCatalogOption(item)}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* 서버 에러 */}
          {purchaseTicket.isError && (
            <p className="text-sm text-red-600 mb-4">
              {serverErrorMessage ?? "수강권 구매에 실패했습니다. 다시 시도해주세요."}
            </p>
          )}

          {/* 버튼 영역 */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isSubmitDisabled}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {purchaseTicket.isPending ? "구매 중..." : "구매"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
